import type { ICompany, ICompanyAffiliation } from 'types';

import { isStytchProdEnv } from './user';

export const getIntercomAppId = () => (!isStytchProdEnv ? 'g3tec45n' : 'i70ex4os');

// Intercom's Company shape — keys (`company_id`, `name`) are Intercom-defined.
export interface IntercomCompany {
  company_id: string;
  name: string;
}

// Maps a user's affiliations into Intercom's `companies` array so support sees
// every company a user belongs to. Companies without an id are skipped.
export const buildIntercomCompanies = (
  affiliationsByCompany?: Record<string, ICompanyAffiliation>,
): IntercomCompany[] =>
  Object.values(affiliationsByCompany ?? {})
    .map((affiliation) => affiliation?.company as unknown as ICompany)
    .filter((company) => Boolean(company?.companyID))
    .map((company) => ({ company_id: company.companyID, name: company.companyName }));

export const updateIntercomSettings = ({ name, email, created_at, role, user_id, companies }) => {
  if (typeof window === 'undefined') return;
  if (!(window as any)?.Intercom) return;
  (window as any).Intercom('boot', {
    api_base: 'https://api-iam.intercom.io',
    app_id: getIntercomAppId(),
    name, // Full name
    email, // Email address
    created_at, // Signup date as a Unix timestamp
    role,
    user_id,
    // All companies the user is affiliated with (Intercom-defined `companies` array)
    ...(companies?.length ? { companies } : {}),
  });
};

const _addIntercomScript = ({ name, email, created_at }) => {
  const APP_ID = getIntercomAppId();

  // Intercom integration script
  const intercomScript = document.createElement('script');
  intercomScript.type = 'text/javascript';
  intercomScript.async = true;
  intercomScript.src = `https://widget.intercom.io/widget/${APP_ID}`;
  const x = document.getElementsByTagName('script')[0];
  x.parentNode.insertBefore(intercomScript, x);

  // Intercom settings
  (window as any).intercomSettings = {
    app_id: APP_ID,
    name, // Full name
    email, // Email address
    created_at, // Signup date as a Unix timestamp
  };

  // Reattach Intercom activator and update settings
  if (typeof (window as any).Intercom === 'function') {
    (window as any).Intercom('reattach_activator');
    (window as any).Intercom('update', (window as any).intercomSettings);
  } else {
    const i = function () {
      // eslint-disable-next-line prefer-rest-params
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    (window as any).Intercom = i;
  }
};

export const isIntercomAvailable = () =>
  typeof window !== 'undefined' && typeof (window as any).Intercom === 'function';

// Opens the in-app messenger. Pass `message` to prefill a new conversation.
export const openIntercomMessenger = (message?: string) => {
  if (!isIntercomAvailable()) return;
  if (message) {
    (window as any).Intercom('showNewMessage', message);
    return;
  }
  (window as any).Intercom('show');
};

// Intercom Events Update
export const trackIntercomEvent = (eventName: string, metadata?: any) => {
  if (typeof window === 'undefined') return;
  if (!(window as any)?.Intercom) return;
  (window as any).Intercom('trackEvent', eventName, metadata);
};

// Auto-close Intercom widget when clicking outside
let isAutoCloseEnabled = false;
let clickHandler: ((event: MouseEvent) => void) | null = null;

export const enableIntercomAutoClose = () => {
  if (typeof window === 'undefined') return;
  if (isAutoCloseEnabled) return; // Prevent multiple event listeners

  clickHandler = (event: MouseEvent) => {
    // Check if Intercom widget is available
    if (!(window as any)?.Intercom) return;

    // Safely check if event.target exists and is an Element
    const target = event.target;
    if (!target || !(target instanceof Element)) return;

    // Helper function to safely check className for both regular and SVG elements
    const getClassNameAsString = (element: Element): string => {
      const className = element.className;
      // Check if className is null or undefined
      if (!className) return '';

      // For SVG elements, className is an SVGAnimatedString with a baseVal property
      // @ts-ignore
      if (typeof className === 'object' && 'baseVal' in className) {
        return (className as any).baseVal || '';
      }
      // For regular HTML elements, className is a string
      return className as string;
    };

    // Check if the clicked element is part of the Intercom widget
    // Intercom widgets are typically in iframes with specific classes/IDs
    const targetClassName = getClassNameAsString(target);
    const parentClassName = target.parentElement ? getClassNameAsString(target.parentElement) : '';

    const isIntercomElement =
      target.closest('#intercom-container') ||
      target.closest('[data-intercom-target]') ||
      target.closest('.intercom-messenger') ||
      target.closest('iframe[src*="intercom"]') ||
      target.closest('.intercom-launcher') ||
      target.closest('.intercom-chat') ||
      // Check for any element with intercom in the class name
      targetClassName.includes('intercom') ||
      // Check for parent elements that might be part of Intercom
      parentClassName.includes('intercom');

    // If click is outside Intercom widget, hide it
    if (!isIntercomElement) {
      try {
        (window as any).Intercom('hide');
      } catch (error) {
        console.warn('Failed to hide Intercom widget:', error);
      }
    }
  };

  // Add event listener to document
  document.addEventListener('click', clickHandler, true);
  isAutoCloseEnabled = true;
};

export const disableIntercomAutoClose = () => {
  if (typeof window === 'undefined') return;

  // Remove the event listener
  if (clickHandler) {
    document.removeEventListener('click', clickHandler, true);
    clickHandler = null;
  }
  isAutoCloseEnabled = false;
};

export const trackIntercomAddPaymentMethod = (companyID: string, adminUserId: string) => {
  trackIntercomEvent('add-payment-method', {
    companyID,
    adminUserId,
  });
};

export const trackIntercomAddTeamMember = (companyID: string, adminUserId: string) => {
  trackIntercomEvent('add-team-member', {
    companyID,
    adminUserId,
  });
};

export const trackIntercomReviewedAInvoice = (companyID: string, adminUserId: string) => {
  trackIntercomEvent('reviewed-invoices', {
    companyID,
    adminUserId,
  });
};

export const trackIntercomPaidAInvoice = (companyID: string, adminUserId: string) => {
  trackIntercomEvent('paid-an-invoice', {
    companyID,
    adminUserId,
  });
};
